/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useEffect, useState, useCallback } from "react";
import { supabase } from "../utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../utils/accountService.js";
import { useSession } from "../components/SessionProvider.jsx";

// Reads the caller's subscription + region entitlements.
//
// We scope these queries EXPLICITLY rather than leaning on RLS to hand back a
// single row. The policy is
//   (user_id = auth.uid()) OR is_account_member(account_id) OR is_platform_admin()
// so it legitimately returns MORE than one row for two ordinary cases:
//   - platform admins (Brand Command staff) see every tenant's row;
//   - anyone who belongs to more than one workspace sees one per workspace.
// `.maybeSingle()` throws on multi-row, which surfaced as a full-page
// "Could not load billing status." the moment a second tenant existed in the
// database. Filtering to the active workspace makes the read deterministic and
// also stops a staff account inheriting another tenant's region entitlements.
export function useBilling() {
  const { user } = useSession();
  const [subscription, setSubscription]   = useState(null);
  const [entitlements, setEntitlements]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // Scope to the active workspace. Fall back to the caller's own row when
      // there's no membership yet (fresh signup, mid-invite).
      const acct = await getMyPrimaryAccount().catch(() => null);
      const scope = (q) =>
        acct?.accountId ? q.eq("account_id", acct.accountId) : q.eq("user_id", user.id);

      const [subRes, entRes] = await Promise.all([
        scope(supabase.from("subscriptions").select("*")).maybeSingle(),
        scope(supabase.from("region_entitlements").select("region_code, status")),
      ]);
      if (subRes.error) throw subRes.error;
      if (entRes.error) throw entRes.error;
      setSubscription(subRes.data ?? null);
      setEntitlements(entRes.data ?? []);
    } catch (e) {
      console.error("[useBilling] load failed:", e);
      setError("Could not load billing status.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setEntitlements([]);
      setLoading(false);
      return;
    }
    refresh();
  }, [user, refresh]);

  const status = subscription?.status ?? null;
  const inGrace = status === "past_due"
    && subscription?.grace_until
    && new Date(subscription.grace_until) > new Date();
  const billingActive = status === "trialing" || status === "active" || !!inGrace;
  const isPro = subscription?.feature_tier === "pro";
  const trialDaysLeft = subscription?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / 86400000))
    : null;
  const entitledRegions = new Set(
    entitlements.filter((e) => e.status === "active").map((e) => e.region_code)
  );

  return {
    subscription,
    entitlements,
    loading,
    error,
    refresh,
    billingActive,
    isPro,
    inGrace,
    trialDaysLeft,
    entitledRegions,
  };
}
